import { CubicFeetPerDay } from "@/libs/units/flowRate";
import type {
  BasicData,
  FlowRatesInSitu,
  FlowRatePrerequisites,
  FlowRatesAtSurface,
  GasProperties
} from "./d";
import {
  getSaturatedGasRateRcfD,
  getTotalInSituGasRateRcfD
} from "../condensatePhase/condensateProperties";

export function getFlowRatesInSitu(
  flowRatePrerequisites: FlowRatePrerequisites,
  gasSeparationEfficiencyRatio: number,
  basicData: BasicData,
  flowRatesAtSurface?: FlowRatesAtSurface,
  flowRatesInSitu?: FlowRatesInSitu,
  flowRatePrerequisitesIn?: FlowRatePrerequisites,
  gasProperties: GasProperties[] = [],
  lockCondensateRate: boolean = false
): FlowRatesInSitu {
  if (
    flowRatesInSitu &&
    flowRatePrerequisites &&
    flowRatePrerequisitesIn &&
    flowRatesAtSurface
  ) {
    return getFlowRatesInSituDownhole(
      flowRatesInSitu,
      flowRatePrerequisitesIn,
      flowRatePrerequisites,
      gasSeparationEfficiencyRatio,
      flowRatesAtSurface,
      basicData,
      lockCondensateRate,
      gasProperties
    );
  } else if (flowRatesAtSurface) {
    return getFlowRatesInSituSurface(
      flowRatesAtSurface,
      flowRatePrerequisites,
      gasSeparationEfficiencyRatio,
      basicData,
      gasProperties
    );
  } else {
    throw new Error("Unable to calculate flow rates");
  }
}

export function getFlowRatesInSituDownhole(
  flowRatesInSituIn: FlowRatesInSitu,
  flowRatePrerequisitesIn: FlowRatePrerequisites,
  flowRatePrerequisites: FlowRatePrerequisites,
  gasSeparationEfficiencyRatio: number,
  flowRatesAtSurface: FlowRatesAtSurface,
  basicData: BasicData,
  lockCondensateRate: boolean = false,
  gasProperties: GasProperties[] = []
): FlowRatesInSitu {
  // q_o
  const oilFlowRateBBLD =
    flowRatesInSituIn.oilFlowRateBBLD /
      (flowRatePrerequisitesIn.oilVolumeFactorBBLSTB /
        flowRatePrerequisites.oilVolumeFactorBBLSTB) || 0;

  // q_w
  const waterFlowRateBBLD =
    flowRatesInSituIn.waterFlowRateBBLD /
      (flowRatePrerequisitesIn.waterVolumeFactorBBLSTB /
        flowRatePrerequisites.waterVolumeFactorBBLSTB) || 0;

  const gasRemainingRatioIn = flowRatesInSituIn.gasRemainingRatio ?? 1;
  const gasRemainingRatio =
    flowRatesInSituIn.gasFlowRateRCFD > 0
      ? Math.min(
          1,
          Math.max(
            0,
            gasRemainingRatioIn *
              (1 -
                flowRatesInSituIn.separatedGasRCFD /
                  flowRatesInSituIn.gasFlowRateRCFD) || 0
          )
        )
      : gasRemainingRatioIn;

  // q_g
  const gasFlowRateRCFD =
    getTotalInSituGasRateRcfD(
      gasProperties,
      basicData.hasGasCompositeValues,
      flowRatesAtSurface.gasFlowRateSCFD,
      flowRatePrerequisites.gasVolumeFactorCFSFCF
    ) * gasRemainingRatio || 0;

  const oilWaterFlowRateBBLD = waterFlowRateBBLD + oilFlowRateBBLD;

  // C69: =(C68/C61)/(C66/C59)
  let gasOilRatioSCFSTB = 0;
  if (
    oilFlowRateBBLD !== 0 &&
    flowRatePrerequisites.oilVolumeFactorBBLSTB !== 0
  ) {
    gasOilRatioSCFSTB =
      gasFlowRateRCFD /
        flowRatePrerequisites.gasVolumeFactorCFSFCF /
        (oilFlowRateBBLD / flowRatePrerequisites.oilVolumeFactorBBLSTB) || 0;
  }

  // C33: =(C17/C9/C10)*C26*C25
  const gasInOilRCFD =
    (flowRatesInSituIn.gasInOilRCFD /
      flowRatePrerequisitesIn.gasVolumeFactorCFSFCF /
      flowRatePrerequisitesIn.oilPhaseGasSolubilitySCFSTB) *
      flowRatePrerequisites.oilPhaseGasSolubilitySCFSTB *
      flowRatePrerequisites.gasVolumeFactorCFSFCF || 0;
  // C34: =(C2*C11)*C9
  const gasInWaterRCFD =
    (flowRatesInSituIn.gasInWaterRCFD /
      flowRatePrerequisitesIn.gasVolumeFactorCFSFCF /
      flowRatePrerequisitesIn.waterPhaseGasSolubilitySCFSTB) *
      flowRatePrerequisites.waterPhaseGasSolubilitySCFSTB *
      flowRatePrerequisites.gasVolumeFactorCFSFCF || 0;

  let condensateRateRCFD = 0;
  const condensateFlowRateSTBD = flowRatesInSituIn.condensateFlowRateSTBD;
  let gasLiquidRatioSCFSTB = 0;
  // Determine the condensate rate and gas-liquid ratio based on whether the condensate rate is locked
  if (lockCondensateRate) {
    condensateRateRCFD = flowRatesInSituIn.condensateRateRCFD;
    gasLiquidRatioSCFSTB =
      flowRatesAtSurface.gasLiquidRatioSCFSTB -
      flowRatePrerequisitesIn.gasCondensateRatioScfStb;
  } else {
    // const condensateRateRCFD =
    //   (flowRatesInSituIn.condensateRateRCFD /
    //     flowRatePrerequisitesIn.gasVolumeFactorCFSFCF /
    //     flowRatePrerequisitesIn.gasCondensateRatioScfStb) *
    //     flowRatePrerequisites.gasCondensateRatioScfStb *
    //     flowRatePrerequisites.gasVolumeFactorCFSFCF || 0;
    // The gas will saturate in the oil and water before it will flash
    // to condensate. So that takes precedence and the RSO and RSW are
    // changing as you move up the wellbore
    condensateRateRCFD =
      (flowRatesAtSurface.waterFlowRateSTBD +
        flowRatesAtSurface.oilFlowRateSTBD) *
      flowRatePrerequisites.gasCondensateRatioScfStb *
      flowRatePrerequisites.gasVolumeFactorCFSFCF;
    gasLiquidRatioSCFSTB = flowRatesAtSurface.gasLiquidRatioSCFSTB;
  }

  // q_c
  const condensateFlowRateBBLD =
    condensateFlowRateSTBD * flowRatePrerequisites.condensateVolumeFactorBBLSTB;

  const liquidFlowRateBBLD = oilWaterFlowRateBBLD + condensateFlowRateBBLD;

  const condensateRatio = condensateFlowRateBBLD / liquidFlowRateBBLD || 0;

  // K27
  const saturatedGasRCFD = getSaturatedGasRateRcfD(
    gasLiquidRatioSCFSTB,
    flowRatePrerequisites.oilPhaseGasSolubilityCorrectedSCFSTB,
    flowRatePrerequisites.waterPhaseGasSolubilityCorrectedSCFSTB,
    flowRatePrerequisites.gasCondensateRatioScfStb,
    flowRatesAtSurface.oilFlowRateSTBD,
    flowRatesAtSurface.waterFlowRateSTBD,
    flowRatePrerequisites.gasVolumeFactorCFSFCF,
    gasFlowRateRCFD,
    condensateRateRCFD
  );

  const gasPreSeparationRCFD =
    gasFlowRateRCFD - saturatedGasRCFD - condensateRateRCFD;

  const separatedGasRCFD = Math.max(
    0,
    gasPreSeparationRCFD * gasSeparationEfficiencyRatio || 0
  );

  const freeGasFlowRateRCFD = Math.max(
    0,
    gasPreSeparationRCFD - separatedGasRCFD || 0
  );

  const freeGasFlowRateBBLD =
    CubicFeetPerDay.toStandardUnit(freeGasFlowRateRCFD);

  const fluidFlowRateBBLD = liquidFlowRateBBLD + freeGasFlowRateBBLD;

  const waterCutRatio = waterFlowRateBBLD / oilWaterFlowRateBBLD || 0;

  return {
    gasFlowRateRCFD, // Q_g
    oilFlowRateBBLD, // Q_o
    waterFlowRateBBLD, // Q_w
    condensateFlowRateBBLD, // Q_c
    condensateFlowRateSTBD,
    liquidFlowRateBBLD, // Q_l
    condensateRatio, // f_c
    fluidFlowRateBBLD,
    gasOilRatioSCFSTB, // GOR
    gasLiquidRatioSCFSTB, // GLR
    waterCutRatio, // WC
    gasInOilRCFD,
    gasInWaterRCFD,
    condensateRateRCFD,
    separatedGasRCFD,
    freeGasFlowRateRCFD,
    freeGasFlowRateBBLD,
    gasRemainingRatio
  };
}

export function getFlowRatesInSituSurface(
  flowRatesAtSurface: FlowRatesAtSurface,
  flowRatePrerequisites: FlowRatePrerequisites,
  gasSeparationEfficiencyRatio: number,
  basicData: BasicData,
  gasProperties: GasProperties[] = []
): FlowRatesInSitu {
  // q_o
  const oilFlowRateBBLD =
    flowRatesAtSurface.oilFlowRateSTBD *
    flowRatePrerequisites.oilVolumeFactorBBLSTB;

  // q_w
  const waterFlowRateBBLD =
    flowRatesAtSurface.waterFlowRateSTBD *
    flowRatePrerequisites.waterVolumeFactorBBLSTB;

  // q_g
  // K25 total in situ gas rate prior to gas sep or saturation
  const gasFlowRateRCFD = getTotalInSituGasRateRcfD(
    gasProperties,
    basicData.hasGasCompositeValues,
    flowRatesAtSurface.gasFlowRateSCFD,
    flowRatePrerequisites.gasVolumeFactorCFSFCF
  );

  const oilWaterFlowRateBBLD = waterFlowRateBBLD + oilFlowRateBBLD;

  // C69: =(C68/C61)/(C66/C59)
  // Don't divide by 0
  let gasOilRatioSCFSTB = 0;
  if (
    oilFlowRateBBLD !== 0 &&
    flowRatePrerequisites.gasVolumeFactorCFSFCF !== 0
  ) {
    gasOilRatioSCFSTB =
      gasFlowRateRCFD /
        flowRatePrerequisites.gasVolumeFactorCFSFCF /
        (oilFlowRateBBLD / flowRatePrerequisites.oilVolumeFactorBBLSTB) || 0;
  }

  // C33: =(C17/C9/C10)*C26*C25
  const gasInOilRCFD =
    flowRatesAtSurface.oilFlowRateSTBD *
    flowRatePrerequisites.oilPhaseGasSolubilitySCFSTB *
    flowRatePrerequisites.gasVolumeFactorCFSFCF;

  // C34: =(C2*C11)*C9
  // need to use water rate instead of flowRatesAtSurface.oilFlowRateSTBD
  const gasInWaterRCFD =
    flowRatesAtSurface.waterFlowRateSTBD *
    flowRatePrerequisites.waterPhaseGasSolubilitySCFSTB *
    flowRatePrerequisites.gasVolumeFactorCFSFCF;

  // K26
  const condensateRateRCFD =
    (flowRatesAtSurface.waterFlowRateSTBD +
      flowRatesAtSurface.oilFlowRateSTBD) *
    flowRatePrerequisites.gasCondensateRatioScfStb *
    flowRatePrerequisites.gasVolumeFactorCFSFCF;

  const condensateFlowRateSTBD =
    flowRatePrerequisites.condensateVolumeFactorBBLSTB > 0
      ? CubicFeetPerDay.toStandardUnit(condensateRateRCFD) /
        flowRatePrerequisites.condensateVolumeFactorBBLSTB
      : 0;

  // q_c
  const condensateFlowRateBBLD =
    condensateFlowRateSTBD * flowRatePrerequisites.condensateVolumeFactorBBLSTB;

  const liquidFlowRateBBLD = oilWaterFlowRateBBLD + condensateFlowRateBBLD;

  const condensateRatio = condensateFlowRateBBLD / liquidFlowRateBBLD || 0;

  // const gasLiquidRatioSCFSTB = gasFlowRateRCFD / oilWaterFlowRateBBLD || 0;
  const gasLiquidRatioSCFSTB = flowRatesAtSurface.gasLiquidRatioSCFSTB;

  // K27
  const saturatedGasRCFD = getSaturatedGasRateRcfD(
    gasLiquidRatioSCFSTB,
    flowRatePrerequisites.oilPhaseGasSolubilityCorrectedSCFSTB,
    flowRatePrerequisites.waterPhaseGasSolubilityCorrectedSCFSTB,
    flowRatePrerequisites.gasCondensateRatioScfStb,
    flowRatesAtSurface.oilFlowRateSTBD,
    flowRatesAtSurface.waterFlowRateSTBD,
    flowRatePrerequisites.gasVolumeFactorCFSFCF,
    gasFlowRateRCFD,
    condensateRateRCFD
  );

  // K29
  const gasPreSeparationRCFD =
    gasFlowRateRCFD - saturatedGasRCFD - condensateRateRCFD;

  // K28
  const separatedGasRCFD = Math.max(
    0,
    gasPreSeparationRCFD * gasSeparationEfficiencyRatio || 0
  );

  // K30 (in situ gas rate post separation)
  const freeGasFlowRateRCFD = Math.max(
    0,
    gasPreSeparationRCFD - separatedGasRCFD || 0
  );

  const freeGasFlowRateBBLD =
    CubicFeetPerDay.toStandardUnit(freeGasFlowRateRCFD);

  const fluidFlowRateBBLD = liquidFlowRateBBLD + freeGasFlowRateBBLD;

  // C34: =(C2*C11)*C9
  const waterCutRatio = flowRatesAtSurface.waterCutRatio;

  return {
    gasFlowRateRCFD, // Q_g
    oilFlowRateBBLD, // Q_o
    waterFlowRateBBLD, // Q_w
    condensateFlowRateBBLD, // Q_c
    condensateFlowRateSTBD,
    liquidFlowRateBBLD, // Q_l
    condensateRatio, // f_c
    fluidFlowRateBBLD,
    gasOilRatioSCFSTB, // GOR
    gasLiquidRatioSCFSTB, // GLR
    waterCutRatio, // WC
    gasInOilRCFD,
    gasInWaterRCFD,
    condensateRateRCFD,
    separatedGasRCFD,
    freeGasFlowRateRCFD,
    freeGasFlowRateBBLD,
    gasRemainingRatio: 1
  };
}
